import { Injectable, signal, computed, effect } from '@angular/core';

export type SidenavSize = 'default' | 'condensed' | 'offcanvas';

const STORAGE_KEY = '__STELA_SIDENAV_SIZE__';
const BREAKPOINT_MOBILE = 768;
const BREAKPOINT_TABLET = 1140;

@Injectable({
    providedIn: 'root'
})
export class SidebarService {
    private readonly html = document.documentElement;

    /** The user's preferred size (persisted to localStorage, used on desktop) */
    private readonly userPreferredSize = signal<SidenavSize>(this.loadPreferredSize());

    /** The effective sidebar size after responsive overrides */
    readonly size = signal<SidenavSize>(this.computeResponsiveSize(window.innerWidth));

    /** Whether the offcanvas drawer is currently open (mobile only) */
    readonly drawerOpen = signal(false);

    /** Convenience computed signals for templates */
    readonly isDefault = computed(() => this.size() === 'default');
    readonly isCondensed = computed(() => this.size() === 'condensed');
    readonly isOffcanvas = computed(() => this.size() === 'offcanvas');

    constructor() {
        // Sync data-sidenav-size attribute on <html> with the size signal
        effect(() => {
            this.html.setAttribute('data-sidenav-size', this.size());
        });

        // Sync sidebar-enable class for offcanvas drawer state
        effect(() => {
            if (this.drawerOpen()) {
                this.html.classList.add('sidebar-enable');
            } else {
                this.html.classList.remove('sidebar-enable');
            }
        });

        // Apply initial attribute
        this.html.setAttribute('data-sidenav-size', this.size());
    }

    /**
     * Called on window resize (from LayoutComponent).
     * Automatically adjusts sidebar mode based on viewport width.
     */
    handleResize(width: number): void {
        this.size.set(this.computeResponsiveSize(width));

        // Close drawer if we resize past mobile
        if (width >= BREAKPOINT_MOBILE && this.drawerOpen()) {
            this.drawerOpen.set(false);
            this.hideBackdrop();
        }
    }

    /**
     * Context-aware toggle called from the hamburger button.
     * - Offcanvas mode: toggle the sliding drawer
     * - Default mode: switch to condensed
     * - Condensed mode: switch to default
     */
    toggle(): void {
        const current = this.size();

        if (current === 'offcanvas') {
            this.toggleDrawer();
            return;
        }

        // Desktop/tablet: toggle between default and condensed
        const next: SidenavSize = current === 'default' ? 'condensed' : 'default';
        this.size.set(next);
        this.userPreferredSize.set(next);
        this.persistSize(next);
        window.dispatchEvent(new Event('resize'));
    }

    /** Toggle offcanvas drawer open/closed with backdrop */
    private toggleDrawer(): void {
        if (this.drawerOpen()) {
            this.closeDrawer();
        } else {
            this.drawerOpen.set(true);
            this.showBackdrop();
        }
    }

    /** Close the offcanvas drawer (called from navigation close button, backdrop click, or menu item click) */
    closeDrawer(): void {
        this.drawerOpen.set(false);
        this.hideBackdrop();
    }

    /** Close sidebar on mobile when a menu item is clicked */
    closeMobile(): void {
        if (this.size() === 'offcanvas') {
            this.closeDrawer();
        }
    }

    private computeResponsiveSize(width: number): SidenavSize {
        if (width < BREAKPOINT_MOBILE) {
            return 'offcanvas';
        }
        if (width <= BREAKPOINT_TABLET) {
            return 'condensed';
        }
        return this.userPreferredSize();
    }

    private loadPreferredSize(): SidenavSize {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored === 'default' || stored === 'condensed') {
                return stored;
            }
        } catch { /* localStorage unavailable */ }
        return 'default';
    }

    private persistSize(size: SidenavSize): void {
        try {
            localStorage.setItem(STORAGE_KEY, size);
        } catch { /* localStorage unavailable */ }
    }

    private showBackdrop(): void {
        if (document.getElementById('sidenav-backdrop')) return;

        const backdrop = document.createElement('div');
        backdrop.id = 'sidenav-backdrop';
        backdrop.className = 'sidenav-backdrop';
        document.body.appendChild(backdrop);
        document.body.style.overflow = 'hidden';

        backdrop.addEventListener('click', () => this.closeDrawer());
    }

    private hideBackdrop(): void {
        const backdrop = document.getElementById('sidenav-backdrop');
        if (backdrop) {
            document.body.removeChild(backdrop);
            document.body.style.overflow = '';
        }
    }
}
